import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  crearClienteAuth,
  entrarConGoogle,
  salir,
  tokenActual,
  type ClienteAuth,
} from "@iavmx/auth/navegador";
// Solo la regla del dominio: el índice de @iavmx/auth trae el middleware (y Postgres) y no va al navegador.
import { dominioPermitido } from "@iavmx/auth/dominios";
import { crearApi, leer, type Api } from "./api";
import type { Yo } from "./tipos";

export interface Configuracion {
  supabaseUrl: string | null;
  supabaseAnonKey: string | null;
  dominiosExtra: string[];
  apps: { portal: string; v360: string; semaforo: string; comercial: string };
}

export type EstadoYo = "sin_sesion" | "cargando" | "ok" | "sin_directorio" | "error";

interface Sesion {
  cargando: boolean;
  configuracion: Configuracion | null;
  cliente: ClienteAuth | null;
  correo: string | null;
  api: Api;
  /** JWT vigente para peticiones que no pasan por el cliente RPC (multipart). */
  token: () => Promise<string | null>;
  yo: Yo | null;
  estadoYo: EstadoYo;
  errorYo: string | null;
  recargarYo: () => Promise<void>;
  /** Google: si el proveedor no está habilitado devuelve un mensaje calmado en vez de tronar. */
  entrarConGoogle: () => Promise<{ error: string | null }>;
  /** Liga mágica solo para cuentas ya invitadas (shouldCreateUser: false). */
  pedirLiga: (correo: string) => Promise<{ error: string | null }>;
  cerrar: () => Promise<void>;
}

const Contexto = React.createContext<Sesion | null>(null);

// Un solo cliente de Auth por pestaña; el API lo lee al pedir el token, no durante el render.
let clienteActual: ClienteAuth | null = null;
const api: Api = crearApi(async () => (clienteActual ? tokenActual(clienteActual) : null));

/** Carga /api/configuracion, crea el cliente de Supabase Auth y expone el API tipado con el token. */
export function SesionProveedor({ children }: { children: React.ReactNode }) {
  const [configuracion, setConfiguracion] = React.useState<Configuracion | null>(null);
  const [cliente, setCliente] = React.useState<ClienteAuth | null>(null);
  const [correo, setCorreo] = React.useState<string | null>(null);
  const [cargando, setCargando] = React.useState(true);
  const qc = useQueryClient();

  React.useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const c = await leer<Configuracion>(await fetch("/api/configuracion"));
        if (cancelado) return;
        setConfiguracion(c);
        if (c.supabaseUrl && c.supabaseAnonKey) {
          const cl = crearClienteAuth(c.supabaseUrl, c.supabaseAnonKey);
          clienteActual = cl;
          setCliente(cl);
          const { data } = await cl.auth.getSession();
          setCorreo(data.session?.user.email ?? null);
          cl.auth.onAuthStateChange((_e, s) => {
            setCorreo(s?.user.email ?? null);
            if (!s) qc.removeQueries({ queryKey: ["yo"] });
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [qc]);

  const yoQuery = useQuery({
    queryKey: ["yo", correo],
    enabled: Boolean(correo),
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const r = await api.api.yo.$get();
      if (r.status === 403) {
        const j = (await r.json()) as { error?: string };
        throw new ErrorDirectorio(j.error ?? "Tu cuenta no está en el directorio.");
      }
      return leer<Yo>(r);
    },
  });

  const estadoYo: EstadoYo = !correo
    ? "sin_sesion"
    : yoQuery.isPending
      ? "cargando"
      : yoQuery.data
        ? "ok"
        : yoQuery.error instanceof ErrorDirectorio
          ? "sin_directorio"
          : "error";

  const valor = React.useMemo<Sesion>(
    () => ({
      cargando,
      configuracion,
      cliente,
      correo,
      api,
      token: async () => (cliente ? tokenActual(cliente) : null),
      yo: yoQuery.data ?? null,
      estadoYo,
      errorYo: yoQuery.error?.message ?? null,
      recargarYo: async () => {
        await qc.invalidateQueries({ queryKey: ["yo"] });
      },
      entrarConGoogle: async () => {
        if (!cliente) return { error: "El acceso no está configurado en este entorno." };
        const { error } = await entrarConGoogle(cliente, `${window.location.origin}/auth/confirmar`);
        if (!error) return { error: null };
        if (/provider|not enabled|unsupported/i.test(error.message))
          return {
            error:
              "Entrar con Google todavía no está disponible en iavmx. Mientras se habilita, entra con la liga que llega a tu correo.",
          };
        return { error: error.message };
      },
      pedirLiga: async (destino) => {
        if (!cliente) return { error: "El acceso no está configurado en este entorno." };
        const limpio = destino.trim().toLowerCase();
        if (!dominioPermitido(limpio, configuracion?.dominiosExtra ?? []))
          return {
            error: "Esa cuenta no es de Vanguardia. iavmx solo acepta cuentas @vanguardia.com.mx.",
          };
        const { error } = await cliente.auth.signInWithOtp({
          email: limpio,
          options: {
            shouldCreateUser: false,
            emailRedirectTo: `${window.location.origin}/auth/confirmar`,
          },
        });
        if (!error) return { error: null };
        if (/signups? not allowed|otp_disabled|user not found/i.test(error.message))
          return {
            error:
              "Ese correo todavía no tiene invitación. Pídesela a tu jefe de área o a quien administra iavmx; cuando te inviten te llega una liga.",
          };
        if (/rate limit|security purposes/i.test(error.message))
          return { error: "Acabamos de mandarte una liga. Espera un minuto antes de pedir otra." };
        return { error: error.message };
      },
      cerrar: async () => {
        if (cliente) await salir(cliente);
        qc.clear();
      },
    }),
    [cargando, configuracion, cliente, correo, yoQuery.data, yoQuery.error, estadoYo, qc],
  );
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

class ErrorDirectorio extends Error {}

export function useSesion(): Sesion {
  const s = React.useContext(Contexto);
  if (!s) throw new Error("useSesion necesita <SesionProveedor>.");
  return s;
}
